import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { fetchWidgetChannels } from 'flow/actions/channels-actions';
import { fetchChatSessions } from 'flow/actions/chat-actions';
import { fetchVoiceMessages } from 'flow/actions/extension-actions';
import useExtension from 'flow/hooks/useExtension';
import useScreenSize from 'flow/hooks/useScreenSize';
import { selectNumberOfCentionErrandsRelevantForUser } from 'flow/selectors/CentionOmniSelectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import {
    selectNumberOfItemsForBadgeInAgentView,
    selectShouldSeeTicketsTab,
} from 'flow/selectors/TicketsSelectors';
import { routes } from 'flow/types/routes';

import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import useAppContext from 'flow/hooks/useAppContext';
import useHasAiStudioAccess from 'flow/pages/ai-studio/hooks/useHasAiStudioAccess';
import { calcTotalUnreadMessages } from 'flow/utils';
import { calculateSelectedTab, isSlimmedTeamsApp } from 'flow/utils/app-utils';
import { calcTotalUnreadVoiceMessages } from 'flow/utils/extension-utils';
import {
    calcTotalUnreadChannelMessages,
    calcTotalUnreadVoiceMessagesForVoicemails,
} from 'flow/utils/pbx-utils';
import { generatePath } from 'flow/utils/router-utils';
import FlowListMenu from '../menu/FlowListMenu';
import AppNavigation from './AppNavigation';
import Icon, { IconKey } from './Icon';
import Menu from './Menu';
import Notification from './Notification';
import Popover from './Popover';

type MenuKey =
    | 'extensions'
    | 'messages'
    | 'pbx'
    | 'calls'
    | 'omni'
    | 'tickets'
    | 'settings'
    | 'ai-studio';

export interface MenuItem {
    iconKey: IconKey;
    key: MenuKey;
    route: string;
    badgeCounter?: number;
}

const ToolbarMenu = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const ref = useRef(null);
    const { width } = useScreenSize();
    const { embedded, i18next } = useAppContext();

    const { ownExtension } = useExtension();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const sessions = useAppSelector((state) => state.chats.get('sessions'));

    const channelsMap = useAppSelector((state) =>
        state.channels.get('channels'),
    );

    const chatUserKey = useAppSelector((state) =>
        state.app.getIn(['extension', 'chatUserKey']),
    );

    const missedCallCount = useAppSelector((state) =>
        state.app.get('missedCallCount'),
    );

    const sharedVoicemails = useAppSelector((state) =>
        state.pbx.get('voicemails'),
    );

    const shouldShowTicketsItem = useAppSelector(selectShouldSeeTicketsTab);

    const shouldShowOmniItem = useAppSelector(
        loggedInUserHasOmnichannelFeature,
    );

    const ticketsBadgeNumber = useAppSelector(
        (state) => selectNumberOfItemsForBadgeInAgentView(state) || 0,
    );

    const { hasFeature } = useFeatureFlags();
    const isThreadEnabled = !!hasFeature(FeatureFlags.THREADS);
    const chatSessionIncludes = isThreadEnabled
        ? ['contact', 'threads']
        : ['contact'];

    const { hasUserAccessAiStudio } = useHasAiStudioAccess();

    const omniBadgeNumber = useAppSelector((state) =>
        shouldShowOmniItem
            ? selectNumberOfCentionErrandsRelevantForUser(state)
            : 0,
    );

    const pbxNotification =
        calcTotalUnreadChannelMessages(channelsMap, chatUserKey) +
        calcTotalUnreadVoiceMessagesForVoicemails(sharedVoicemails);

    const selectedTab = calculateSelectedTab(location);

    const totalNewCallBadgeItems =
        calcTotalUnreadVoiceMessages(ownExtension.get('voiceMessages')) +
        missedCallCount;

    useEffect(() => {
        dispatch(fetchVoiceMessages(ownExtensionKey, 'contact'));
        dispatch(fetchWidgetChannels('apps'));
        dispatch(fetchChatSessions(...chatSessionIncludes));
    }, [dispatch, ownExtensionKey]);

    if (width >= 790) {
        return <AppNavigation />;
    }

    const menuItems: MenuItem[] = [
        {
            iconKey: 'accountBox',
            key: 'extensions',
            route: routes['extensions'],
        },
        ...(isSlimmedTeamsApp(embedded)
            ? []
            : [
                  {
                      iconKey: 'chat' as IconKey,
                      key: 'messages' as MenuKey,
                      route: routes['messages'],
                      badgeCounter: calcTotalUnreadMessages(sessions),
                  },
                  {
                      iconKey: 'ivr' as IconKey,
                      key: 'pbx' as MenuKey,
                      route: routes['pbx'],
                      badgeCounter: pbxNotification,
                  },
              ]),
        {
            iconKey: 'callHistory',
            key: 'calls',
            route: routes['calls'],
            badgeCounter: totalNewCallBadgeItems,
        },
        ...(shouldShowTicketsItem
            ? [
                  {
                      iconKey: 'headset' as IconKey,
                      key: 'tickets' as MenuKey,
                      route: routes['tickets'],
                      badgeCounter: ticketsBadgeNumber,
                  },
              ]
            : []),
        ...(shouldShowOmniItem
            ? [
                  {
                      iconKey: 'support_agent' as IconKey,
                      key: 'omni' as MenuKey,
                      route: routes['omni'],
                      badgeCounter: omniBadgeNumber,
                  },
              ]
            : []),
        ...(hasUserAccessAiStudio
            ? [
                  {
                      iconKey: 'autoAwesome' as IconKey,
                      key: 'ai-studio' as MenuKey,
                      route: routes['ai-studio.page'],
                  },
              ]
            : []),
        {
            iconKey: 'general',
            key: 'settings',
            route: generatePath(routes['settings']),
        },
    ];

    // calculateSelectedTab hands back a raw path segment, or null, so it is
    // matched against the menu rather than trusted as a key.
    const selectedItem = menuItems.find((item) => item.key === selectedTab);
    const selectedTabBadgeCounter = selectedItem?.badgeCounter || 0;

    const combineBadgeCounter = menuItems.reduce((acc, item) => {
        if (item.key === selectedTab) return acc;
        return item.badgeCounter ? acc + item.badgeCounter : acc;
    }, 0);

    const handleClose = () => {
        ref.current?.click();
    };

    return (
        <div>
            <Popover
                backgroundColor="bg-gray-200 "
                className="flex p-0"
                icon={
                    <div
                        className="inline-flex rounded-sm text-sm/4 text-white"
                        data-test-id="drawer-menu"
                        ref={ref}
                    >
                        {/*
                         * Hidden text rather than a triggerLabel: an
                         * aria-label would replace the button's content, and
                         * the two unread counters below are part of what a
                         * screen reader should hear. Reads as
                         * "Navigation menu, Messages 5 12".
                         */}
                        <span className="sr-only">
                            {i18next.t('navigation.menu')}
                        </span>
                        <div className="bg-main relative rounded-sm px-4 py-2">
                            {selectedItem && (
                                <span className="sr-only">
                                    {i18next.t(`route.${selectedItem.key}`)}
                                </span>
                            )}
                            {selectedTabBadgeCounter > 0 && (
                                <Notification>
                                    {selectedTabBadgeCounter}
                                </Notification>
                            )}
                            <Icon iconKey={selectedItem?.iconKey} />
                        </div>

                        <div className="relative p-2 text-gray-500">
                            {combineBadgeCounter > 0 && (
                                <Notification>
                                    {combineBadgeCounter}
                                </Notification>
                            )}
                            <Icon iconKey="arrow_down" />
                        </div>
                    </div>
                }
            >
                <Menu className="start-1">
                    <FlowListMenu
                        menuItems={menuItems}
                        handleClose={handleClose}
                    />
                </Menu>
            </Popover>
        </div>
    );
};

export default ToolbarMenu;
